import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Cookie, Mail, Download } from "lucide-react";

export default function PopupsPage() {
  const [cfg, setCfg] = useState(null);
  const [subs, setSubs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/popups").then((r) => setCfg(r.data));
    api.get("/admin/newsletter").then((r) => setSubs(r.data)).catch(() => {});
  }, []);

  const setField = (section, key, value) =>
    setCfg((c) => ({ ...c, [section]: { ...c[section], [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/popups", cfg);
      toast.success("Popups updated");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const exportCsv = () => {
    const rows = [["email", "subscribed_at"], ...subs.map((s) => [s.email, s.created_at])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "newsletter_subscribers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (!cfg) return <div className="flex items-center justify-center h-96"><Loader2 className="animate-spin text-zinc-400" /></div>;

  return (
    <div className="animate-fade-up max-w-3xl pb-16" data-testid="popups-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-zinc-500">Storefront</span>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-1">Popups</h1>
          <p className="text-zinc-500 font-body mt-1">Enable and edit the popups shown to visitors across your storefronts.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="popups-save" className="casper-btn rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
          {saving && <Loader2 size={14} className="animate-spin" />} Save changes
        </button>
      </div>

      {/* Maintenance */}
      <Section icon={AlertTriangle} title="Maintenance popup" desc="A blocking-style modal telling visitors the store is still in testing."
        enabled={cfg.maintenance.enabled} onToggle={(v) => setField("maintenance", "enabled", v)} testid="maintenance">
        <F label="Title"><input data-testid="maint-title" value={cfg.maintenance.title} onChange={(e) => setField("maintenance", "title", e.target.value)} className="fin" /></F>
        <F label="Message"><textarea data-testid="maint-message" rows={3} value={cfg.maintenance.message} onChange={(e) => setField("maintenance", "message", e.target.value)} className="fin" /></F>
        <F label="Button label"><input data-testid="maint-button" value={cfg.maintenance.button_label} onChange={(e) => setField("maintenance", "button_label", e.target.value)} className="fin" /></F>
      </Section>

      {/* Cookie */}
      <Section icon={Cookie} title="Cookie consent" desc="A bottom banner with accept / decline options."
        enabled={cfg.cookie.enabled} onToggle={(v) => setField("cookie", "enabled", v)} testid="cookie">
        <F label="Message"><textarea data-testid="cookie-message-input" rows={3} value={cfg.cookie.message} onChange={(e) => setField("cookie", "message", e.target.value)} className="fin" /></F>
        <div className="grid grid-cols-2 gap-4">
          <F label="Accept label"><input data-testid="cookie-accept-input" value={cfg.cookie.accept_label} onChange={(e) => setField("cookie", "accept_label", e.target.value)} className="fin" /></F>
          <F label="Decline label"><input data-testid="cookie-decline-input" value={cfg.cookie.decline_label} onChange={(e) => setField("cookie", "decline_label", e.target.value)} className="fin" /></F>
        </div>
      </Section>

      {/* Newsletter */}
      <Section icon={Mail} title="Newsletter popup" desc="A timed email-capture modal. Subscribers are collected below."
        enabled={cfg.newsletter.enabled} onToggle={(v) => setField("newsletter", "enabled", v)} testid="newsletter">
        <F label="Title"><input data-testid="news-title" value={cfg.newsletter.title} onChange={(e) => setField("newsletter", "title", e.target.value)} className="fin" /></F>
        <F label="Message"><textarea data-testid="news-message" rows={3} value={cfg.newsletter.message} onChange={(e) => setField("newsletter", "message", e.target.value)} className="fin" /></F>
        <div className="grid grid-cols-2 gap-4">
          <F label="Button label"><input data-testid="news-button" value={cfg.newsletter.button_label} onChange={(e) => setField("newsletter", "button_label", e.target.value)} className="fin" /></F>
          <F label="Show after (seconds)"><input data-testid="news-delay" type="number" min="0" value={cfg.newsletter.delay} onChange={(e) => setField("newsletter", "delay", parseInt(e.target.value || "0", 10))} className="fin" /></F>
        </div>

        <div className="border-t border-zinc-100 pt-4 mt-2">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-heading font-semibold">Subscribers <span className="text-zinc-400">({subs.length})</span></div>
            {subs.length > 0 && (
              <button onClick={exportCsv} data-testid="news-export" className="flex items-center gap-1.5 text-xs font-semibold border border-zinc-200 rounded-full px-3 py-1.5 hover:bg-zinc-50">
                <Download size={12} /> Export CSV
              </button>
            )}
          </div>
          {subs.length === 0 ? (
            <p className="text-sm text-zinc-400">No subscribers yet.</p>
          ) : (
            <div className="max-h-56 overflow-y-auto border border-zinc-100 rounded-xl divide-y divide-zinc-100" data-testid="news-subscribers">
              {subs.map((s) => (
                <div key={s.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="font-body">{s.email}</span>
                  <span className="text-zinc-400 text-xs">{(s.created_at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      <style>{`.fin{width:100%;border:1px solid #e4e4e7;border-radius:12px;padding:10px 14px;font-size:14px;outline:none;font-family:'Plus Jakarta Sans'}.fin:focus{border-color:#4F46E5}`}</style>
    </div>
  );
}

const Section = ({ icon: Icon, title, desc, enabled, onToggle, testid, children }) => (
  <div className="bg-white border border-zinc-200/80 rounded-xl p-6 mb-6" data-testid={`section-${testid}`}>
    <div className="flex items-start justify-between gap-4 mb-5">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-zinc-100 flex items-center justify-center shrink-0"><Icon size={18} /></div>
        <div>
          <h2 className="font-heading font-medium text-lg tracking-tight">{title}</h2>
          <p className="text-sm text-zinc-500">{desc}</p>
        </div>
      </div>
      <button
        role="switch" aria-checked={enabled}
        onClick={() => onToggle(!enabled)}
        data-testid={`toggle-${testid}`}
        className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${enabled ? "bg-black" : "bg-zinc-200"}`}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${enabled ? "translate-x-[22px]" : "translate-x-0.5"}`} />
      </button>
    </div>
    <div className={`space-y-4 ${enabled ? "" : "opacity-50 pointer-events-none"}`}>{children}</div>
  </div>
);

const F = ({ label, children }) => (
  <div>
    <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">{label}</label>
    {children}
  </div>
);
